import { useMutation } from "@apollo/client";
import { Typography } from "@mui/material";
import { Grid, Stack } from "@mui/system";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import {
  documentKindNames,
  manifestNumberLabel,
} from "encamp-shared/src/waste/shippingDocumentLabels";
import { WasteLineUnified } from "generated-graphql/graphql";
import { useProcessedManifest } from "hooks/useProcessedManifest";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useCallback } from "react";
import { formatNumber } from "util/formatNumber";

import { PROCESSED_MANIFESTS_QUERY } from "../Manifests/api";
import { DELETE_WASTE_LINE_UNIFIED_MUTATION, WASTE_LINES_UNIFIED_QUERY } from "./api";

interface DeleteLineItemDialogProps {
  lineItem: Pick<
    WasteLineUnified,
    | "id"
    | "lineItem"
    | "documentKind"
    | "manifestTrackingNumber"
    | "vendorAmount"
    | "shippedDate"
    | "processedManifestId"
    | "isEManifest"
  >;
  onClose: () => void;
  onDeleteConfirm: () => void;
}

export const DeleteLineItemDialog = ({
  lineItem,
  onClose,
  onDeleteConfirm,
}: DeleteLineItemDialogProps) => {
  const alerts = useAlerts();
  const { popIdFromUrl } = useSelectedIdInUrl("wasteLineUnifiedId");
  const { processedManifest } = useProcessedManifest(lineItem.processedManifestId);
  const [deleteLineItem, { loading }] = useMutation(DELETE_WASTE_LINE_UNIFIED_MUTATION);

  const handleDelete = useCallback(async () => {
    try {
      await deleteLineItem({
        variables: { id: lineItem.id },
        refetchQueries: [WASTE_LINES_UNIFIED_QUERY, PROCESSED_MANIFESTS_QUERY],
      });
      alerts.info(
        `Vendor line item deleted successfully!${
          lineItem.isEManifest ? ` Now showing e‑Manifest line item data.` : ""
        }`
      );
      popIdFromUrl();
      onDeleteConfirm();
    } catch (err) {
      alerts.error(`There was an error deleting the line item`, err);
    }
  }, [alerts, deleteLineItem, lineItem.id, lineItem.isEManifest, onDeleteConfirm, popIdFromUrl]);

  return (
    <ConfirmDialog
      open
      loading={loading}
      onClose={onClose}
      onConfirm={handleDelete}
      header={`Are you sure you want to delete ${
        processedManifest?.vendor ?? "vendor"
      } data for this line item?`}
      info={
        <Stack spacing={2}>
          <Typography variant="body1" color="textSecondary">
            This action cannot be undone.
          </Typography>
          <Grid container columnSpacing={2} rowSpacing={1}>
            <Grid size={3}>
              <Typography variant="caption" color="textSecondary">
                Line Item
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography>{lineItem.lineItem}</Typography>
            </Grid>
            <Grid size={3}>
              <Typography variant="caption" color="textSecondary">
                {
                  documentKindNames(lineItem.documentKind, lineItem.manifestTrackingNumber)
                    .numberLabel
                }
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography>{manifestNumberLabel(lineItem)}</Typography>
            </Grid>
            <Grid size={3}>
              <Typography variant="caption" color="textSecondary">
                Amount
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography>{`${formatNumber(lineItem.vendorAmount)} lbs`}</Typography>
            </Grid>
          </Grid>
        </Stack>
      }
    />
  );
};
